/**
 * GatewayHealthCard (OCP-555) - managed-AI gateway readiness, operator-side.
 *
 * On 2026-08-03/04 the gateway returned intermittent `503 no_db_connection` for
 * roughly 14 hours and the only place it showed was a customer's error stream.
 * OCP now probes the gateway's readiness endpoint every minute; this card is
 * where an operator sees the result: current state, whether an alert episode is
 * open, a 24h summary, and a strip of the recent observations so a past flap is
 * still visible after it stopped.
 *
 * Lives on the AI gateway page next to the model list (OCP-519) - steer and
 * observe the same dependency in one place. Self-fetching so the page's model
 * list and this card fail independently: a health-read failure must never blank
 * the model table.
 */
import { useCallback, useEffect, useState } from 'react';

import { api, ApiError } from '../lib/api';
import { fmtDateTime } from '../lib/format-time';

type GatewayHealthStatus = 'healthy' | 'degraded' | 'unhealthy';

interface HealthSample {
  observed_at: string;
  status: GatewayHealthStatus;
  db_status: string | null;
  http_status: number | null;
  latency_ms: number | null;
  error: string | null;
}

interface HealthResponse {
  configured: boolean;
  probe_url: string | null;
  current: HealthSample | null;
  alert: {
    alerting: boolean;
    fail_streak: number;
    since: string | null;
    last_alerted_at: string | null;
    last_reason: string | null;
  };
  window: {
    hours: number;
    probes: number;
    healthy: number;
    degraded: number;
    unhealthy: number;
    healthy_pct: number | null;
    db_disconnected: number;
    avg_latency_ms: number | null;
    max_latency_ms: number | null;
  };
  history: HealthSample[];
}

/** How many of the newest samples the strip renders. */
const STRIP_SAMPLES = 60;

const STATUS_LABEL: Record<GatewayHealthStatus, string> = {
  healthy: 'Healthy',
  degraded: 'Degraded',
  unhealthy: 'Unreachable',
};

/** One shared palette for the badge, the strip and the summary tiles. */
function statusClasses(status: GatewayHealthStatus): string {
  if (status === 'healthy') {
    return 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 ring-green-200 dark:ring-green-800';
  }
  if (status === 'degraded') {
    return 'bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 ring-amber-200 dark:ring-amber-800';
  }
  return 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 ring-red-200 dark:ring-red-800';
}

function stripClasses(status: GatewayHealthStatus): string {
  if (status === 'healthy') return 'bg-green-500/80';
  if (status === 'degraded') return 'bg-amber-500';
  return 'bg-red-500';
}

function formatTime(iso: string): string {
  return fmtDateTime(iso);
}

function relativeMinutes(iso: string): string {
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60_000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `${hours} h ago`;
  return `${Math.round(hours / 24)} d ago`;
}

export function GatewayHealthCard() {
  const [data, setData] = useState<HealthResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await api<HealthResponse>(`/v1/admin/gateway/health?limit=${STRIP_SAMPLES}`);
      setData(res);
    } catch (err) {
      setLoadError(
        err instanceof ApiError
          ? `Could not load gateway health (HTTP ${err.status}).`
          : 'Could not load gateway health.',
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <section
      data-testid="gateway-health-card"
      className="mt-6 rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 shadow-sm"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Gateway health</h2>
          <p className="mt-1 max-w-2xl text-xs text-gray-500 dark:text-gray-400">
            orboto Control probes the gateway&apos;s readiness endpoint every minute and alerts you when it
            stops answering or loses its own auth database - the failure that returns 503s to every tenant
            AI call. History is kept so a past flap stays visible.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          disabled={loading}
          className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-xs font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
        >
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {loadError && (
        <div
          role="alert"
          className="mt-4 rounded border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300"
        >
          {loadError}
        </div>
      )}

      {!loadError && data && !data.configured && (
        <div className="mt-4 rounded border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 px-3 py-2 text-sm text-amber-800 dark:text-amber-300">
          No gateway base URL configured - nothing is being probed. Managed AI is off fleet-wide.
        </div>
      )}

      {!loadError && data && data.configured && (
        <>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            {data.current ? (
              <span
                data-testid="gateway-health-status"
                className={`inline-flex items-center rounded px-2 py-1 text-xs font-semibold uppercase tracking-wide ring-1 ring-inset ${statusClasses(data.current.status)}`}
              >
                {STATUS_LABEL[data.current.status]}
              </span>
            ) : (
              <span className="inline-flex items-center rounded bg-gray-100 dark:bg-gray-800 px-2 py-1 text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400 ring-1 ring-inset ring-gray-200 dark:ring-gray-700">
                No probe yet
              </span>
            )}
            {data.current && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                last probe {relativeMinutes(data.current.observed_at)}
                {data.current.latency_ms !== null ? ` · ${data.current.latency_ms} ms` : ''}
              </span>
            )}
            {data.probe_url && (
              <span className="font-mono text-xs text-gray-400 break-all">{data.probe_url}</span>
            )}
          </div>

          {data.current?.error && (
            <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">{data.current.error}</p>
          )}

          {data.alert.alerting && (
            <div
              role="alert"
              className="mt-4 rounded border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300"
            >
              <span className="font-semibold">Alert open</span>
              {data.alert.since ? ` since ${formatTime(data.alert.since)}` : ''} ·{' '}
              {data.alert.fail_streak} failed probe(s) in a row.
              {data.alert.last_reason ? ` ${data.alert.last_reason}.` : ''}
            </div>
          )}

          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatTile label={`Healthy (${data.window.hours}h)`}>
              {data.window.healthy_pct === null ? '-' : `${data.window.healthy_pct}%`}
            </StatTile>
            <StatTile label="Probes">{data.window.probes}</StatTile>
            <StatTile label="Auth DB misses">{data.window.db_disconnected}</StatTile>
            <StatTile label="Latency avg / max">
              {data.window.avg_latency_ms === null
                ? '-'
                : `${data.window.avg_latency_ms} / ${data.window.max_latency_ms ?? '-'} ms`}
            </StatTile>
          </div>

          {data.history.length > 0 && (
            <div className="mt-4">
              <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
                Recent probes (oldest to newest)
              </div>
              <div className="mt-2 flex gap-[2px]" data-testid="gateway-health-strip">
                {[...data.history].reverse().map((s) => (
                  <span
                    key={s.observed_at}
                    title={`${formatTime(s.observed_at)} - ${STATUS_LABEL[s.status]}${s.error ? `: ${s.error}` : ''}`}
                    className={`h-6 flex-1 rounded-sm ${stripClasses(s.status)}`}
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
}

function StatTile({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/40 p-3">
      <div className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</div>
      <div className="mt-1 text-sm font-semibold text-gray-900 dark:text-gray-100">{children}</div>
    </div>
  );
}
